/**
 * Empty Bin's instance of the generic job machinery (#969): its tracker. How a
 * job is followed — socket first, status route as the safety net — is in
 * `job-tracker.ts`. A reload needs no record of its own: the Bin listing says
 * when an Empty Bin is running (`BinResponse.emptying`).
 */
import type { OperationProgressEvent } from '@papan/realtime/client'
import { createJobTracker, requestOutcomeUnknown, type JobTracker, type JobTrackerDeps, type JobTrackerTimings, type JobProgress } from './job-tracker.js'

export interface EmptyBinJobOutcome {
  /** Bin entries removed. */
  deleted: number
}

export type EmptyBinTrackerDeps = Omit<JobTrackerDeps<EmptyBinJobOutcome>, 'kind' | 'readResult'>

export type EmptyBinTracker = JobTracker<EmptyBinJobOutcome> & {
  follow: (jobId: string, operationId: string, onProgress?: (p: JobProgress) => void) => Promise<EmptyBinJobOutcome>
}

/** A completed Empty Bin event carries how many entries it removed. */
function readEmptyBinResult(e: OperationProgressEvent): EmptyBinJobOutcome | null {
  return typeof e.deleted === 'number' ? { deleted: e.deleted } : null
}

export function createEmptyBinTracker(
  deps: EmptyBinTrackerDeps,
  ownOperationId: string,
  timings: JobTrackerTimings = {},
): EmptyBinTracker {
  return createJobTracker({ ...deps, kind: 'empty-bin', readResult: readEmptyBinResult }, ownOperationId, timings)
}

export interface EmptyBinRunHooks {
  /** The job was queued (or joined): which job, and how many entries it takes. */
  onQueued?: (job: { jobId: string; operationId: string; total: number }) => void
  onProgress?: (done: number, total: number) => void
}

export interface EmptyBinRunDeps {
  /** Ask for the Empty Bin (`filesApi.emptyBinAsync`). */
  start: (operationId: string) => Promise<
    | { kind: 'done'; deleted: number }
    | { kind: 'queued'; jobId: string; operationId: string; total: number }
  >
  getStatus: EmptyBinTrackerDeps['getStatus']
  /** The tab's realtime socket; absent ⇒ the status route is polled instead. */
  onOperationEvent?: EmptyBinTrackerDeps['onOperationEvent']
  isConnected?: () => boolean
}

/**
 * A person's Empty Bin, end to end (#969): the request, then the background
 * job it queued, followed over the websocket with the status route as the
 * safety net. Resolves with the entries removed when the job ends; rejects with
 * `JobFailedError` / `JobLostError`, or the request's own error. Used by the
 * Bin page and Settings → Storage.
 */
export async function runEmptyBinJob(deps: EmptyBinRunDeps, hooks: EmptyBinRunHooks = {}): Promise<number> {
  const operationId = `empty-bin-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const tracker = createEmptyBinTracker({
    onOperationEvent: deps.onOperationEvent ?? (() => () => {}),
    isConnected: deps.isConnected ?? (() => false),
    getStatus: deps.getStatus,
  }, operationId)
  let started: Awaited<ReturnType<EmptyBinRunDeps['start']>>
  try {
    try {
      started = await deps.start(operationId)
    } catch (err) {
      // No answer arrived, so the server may have queued it anyway. Asking
      // again is safe: a second Empty Bin joins the running job.
      if (!requestOutcomeUnknown(err)) throw err
      started = await deps.start(operationId)
    }
  } catch (err) {
    tracker.dispose()
    throw err
  }
  if (started.kind === 'done') {
    tracker.dispose()
    return started.deleted
  }
  const job = started
  hooks.onQueued?.({ jobId: job.jobId, operationId: job.operationId, total: job.total })
  hooks.onProgress?.(0, job.total)
  const outcome = await tracker.follow(job.jobId, job.operationId, (p) => hooks.onProgress?.(p.done, p.total || job.total))
  return outcome.deleted
}
